export const formatTime = timeInSeconds => {
  if (!timeInSeconds) {
    return '';
  }

  if (timeInSeconds < 60) {
    return `${timeInSeconds}s`;
  }

  if (timeInSeconds < 3600) {
    const minutes = Math.floor(timeInSeconds / 60);
    return `${minutes}m`;
  }

  if (timeInSeconds < 86400) {
    const hours = Math.floor(timeInSeconds / 3600);
    return `${hours}h`;
  }

  const days = Math.floor(timeInSeconds / 86400);
  return `${days}d`;
};

export const GROUP_BY_FILTER = {
  1: { id: 1, period: 'day' },
  2: { id: 2, period: 'week' },
  3: { id: 3, period: 'month' },
  4: { id: 4, period: 'year' },
};

export const GROUP_BY_OPTIONS = {
  DAY: {
    id: 'DAY',
    period: 'day',
    translationKey: 'REPORT.GROUPING_OPTIONS.DAY',
  },
  WEEK: {
    id: 'WEEK',
    period: 'week',
    translationKey: 'REPORT.GROUPING_OPTIONS.WEEK',
  },
  MONTH: {
    id: 'MONTH',
    period: 'month',
    translationKey: 'REPORT.GROUPING_OPTIONS.MONTH',
  },
  YEAR: {
    id: 'YEAR',
    period: 'year',
    translationKey: 'REPORT.GROUPING_OPTIONS.YEAR',
  },
};

export const DATE_RANGE_OPTIONS = {
  LAST_7_DAYS: {
    id: 'LAST_7_DAYS',
    translationKey: 'REPORT.DATE_RANGE_OPTIONS.LAST_7_DAYS',
    offset: 6,
    groupByOptions: [GROUP_BY_OPTIONS.DAY],
  },
  LAST_30_DAYS: {
    id: 'LAST_30_DAYS',
    translationKey: 'REPORT.DATE_RANGE_OPTIONS.LAST_30_DAYS',
    offset: 29,
    groupByOptions: [GROUP_BY_OPTIONS.DAY, GROUP_BY_OPTIONS.WEEK],
  },
  LAST_3_MONTHS: {
    id: 'LAST_3_MONTHS',
    translationKey: 'REPORT.DATE_RANGE_OPTIONS.LAST_3_MONTHS',
    offset: 89,
    groupByOptions: [
      GROUP_BY_OPTIONS.DAY,
      GROUP_BY_OPTIONS.WEEK,
      GROUP_BY_OPTIONS.MONTH,
    ],
  },
  LAST_6_MONTHS: {
    id: 'LAST_6_MONTHS',
    translationKey: 'REPORT.DATE_RANGE_OPTIONS.LAST_6_MONTHS',
    offset: 179,
    groupByOptions: [GROUP_BY_OPTIONS.WEEK, GROUP_BY_OPTIONS.MONTH],
  },
  LAST_YEAR: {
    id: 'LAST_YEAR',
    translationKey: 'REPORT.DATE_RANGE_OPTIONS.LAST_YEAR',
    offset: 364,
    groupByOptions: [GROUP_BY_OPTIONS.WEEK, GROUP_BY_OPTIONS.MONTH],
  },
  CUSTOM_DATE_RANGE: {
    id: 'CUSTOM_DATE_RANGE',
    translationKey: 'REPORT.DATE_RANGE_OPTIONS.CUSTOM_DATE_RANGE',
    offset: null,
    groupByOptions: [
      GROUP_BY_OPTIONS.DAY,
      GROUP_BY_OPTIONS.WEEK,
      GROUP_BY_OPTIONS.MONTH,
      GROUP_BY_OPTIONS.YEAR,
    ],
  },
};

export const CHART_FONT_FAMILY =
  'Inter,-apple-system,system-ui,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,sans-serif';

export const DEFAULT_LINE_CHART = {
  type: 'line',
  fill: false,
  borderColor: '#779BBB',
  pointBackgroundColor: '#779BBB',
};

export const DEFAULT_BAR_CHART = {
  type: 'bar',
  backgroundColor: 'rgb(31, 147, 255)',
};

const SMALL_COUNT_TICK_LIMIT = 20;
const DEFAULT_MAX_TICKS_LIMIT = 8;
const DURATION_MAX_TICKS_LIMIT = 6;

const toFiniteNumber = value => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

const normalizeMaxValue = value =>
  Math.max(0, Math.ceil(toFiniteNumber(value)));

const createXAxisScale = () => ({
  ticks: {
    fontFamily: CHART_FONT_FAMILY,
  },
  grid: {
    drawOnChartArea: false,
  },
});

const createYAxisScale = ticks => ({
  type: 'linear',
  position: 'left',
  beginAtZero: true,
  ticks: {
    fontFamily: CHART_FONT_FAMILY,
    ...ticks,
  },
  grid: {
    drawOnChartArea: false,
  },
});

const shouldUseUnitCountTicks = maxValue => {
  const normalizedMaxValue = normalizeMaxValue(maxValue);
  return normalizedMaxValue > 0 && normalizedMaxValue <= SMALL_COUNT_TICK_LIMIT;
};

const formatDurationTick = value => {
  const seconds = normalizeMaxValue(value);
  return seconds ? formatTime(seconds) : '0s';
};

export const countChartScale = maxValue => {
  const useUnitTicks = shouldUseUnitCountTicks(maxValue);

  return createYAxisScale({
    precision: 0,
    ...(useUnitTicks
      ? { stepSize: 1 }
      : { maxTicksLimit: DEFAULT_MAX_TICKS_LIMIT }),
    callback: (value, index, ticks) => {
      const tickValue = toFiniteNumber(value);

      if (useUnitTicks && Number.isInteger(tickValue)) {
        return tickValue;
      }

      if (!index || index === ticks.length - 1) {
        return tickValue;
      }

      return '';
    },
  });
};

export const durationChartScale = maxSeconds =>
  createYAxisScale({
    precision: 0,
    maxTicksLimit:
      normalizeMaxValue(maxSeconds) > 0 ? DURATION_MAX_TICKS_LIMIT : 2,
    callback: value => formatDurationTick(value),
  });

export const maxChartValueFromData = data => {
  if (!Array.isArray(data)) {
    return 0;
  }

  return data.reduce((maxValue, item) => {
    return Math.max(maxValue, toFiniteNumber(item?.value));
  }, 0);
};

const TIME_METRIC_KEYS = [
  'avg_first_response_time',
  'reply_time',
  'avg_resolution_time',
];

export const isTimeChartMetric = metricKey =>
  TIME_METRIC_KEYS.includes(metricKey);

export const chartScaleForMetric = (metricKey, maxValue) => {
  return isTimeChartMetric(metricKey)
    ? durationChartScale(maxValue)
    : countChartScale(maxValue);
};

const createChartConfig = yAxisScale => ({
  datasets: [DEFAULT_BAR_CHART],
  scales: {
    x: createXAxisScale(),
    y: yAxisScale,
  },
});

export const DEFAULT_CHART = createChartConfig(countChartScale());

export const TIME_CHART_CONFIG = createChartConfig(durationChartScale());

export const METRIC_CHART = {
  conversations_count: DEFAULT_CHART,
  incoming_messages_count: DEFAULT_CHART,
  outgoing_messages_count: DEFAULT_CHART,
  avg_first_response_time: TIME_CHART_CONFIG,
  reply_time: TIME_CHART_CONFIG,
  avg_resolution_time: TIME_CHART_CONFIG,
  resolutions_count: DEFAULT_CHART,
  bot_resolutions_count: DEFAULT_CHART,
  bot_handoffs_count: DEFAULT_CHART,
};

export const OVERVIEW_METRICS = {
  open: 'OPEN',
  unattended: 'UNATTENDED',
  unassigned: 'UNASSIGNED',
  pending: 'PENDING',
  online: 'ONLINE',
  busy: 'BUSY',
  offline: 'OFFLINE',
};
